import { useEffect, useRef } from 'react';
import { useDashboardTab } from '@/context/DashboardTabContext';

interface UseChecklistAutoCompleteOptions {
  completedSteps: Record<string, boolean>;
  markStepCompleted: (key: string) => void;
  onboardingMode: 'guided' | 'self' | null;
}

/**
 * Listens for user actions and auto-marks checklist items as complete.
 * Runs as a side effect inside OnboardingProvider.
 */
export function useChecklistAutoComplete({
  completedSteps,
  markStepCompleted,
  onboardingMode,
}: UseChecklistAutoCompleteOptions) {
  const { businessDashboardTab } = useDashboardTab();
  const prevTabRef = useRef(businessDashboardTab);

  // Auto-complete explore_analytics when user visits insights tab
  useEffect(() => {
    if (!onboardingMode) return;
    if (
      businessDashboardTab === 'insights' &&
      prevTabRef.current !== 'insights' &&
      !completedSteps['explore_analytics']
    ) {
      markStepCompleted('explore_analytics');
    }
    prevTabRef.current = businessDashboardTab;
  }, [businessDashboardTab, onboardingMode, completedSteps, markStepCompleted]);

  // Auto-complete try_ai_chat when AI chat opens
  useEffect(() => {
    if (!onboardingMode || completedSteps['try_ai_chat']) return;

    const handleAIChatOpen = () => {
      markStepCompleted('try_ai_chat');
    };

    window.addEventListener('ai-chat-opened', handleAIChatOpen);
    window.addEventListener('open-ai-chat', handleAIChatOpen);
    return () => {
      window.removeEventListener('ai-chat-opened', handleAIChatOpen);
      window.removeEventListener('open-ai-chat', handleAIChatOpen);
    };
  }, [onboardingMode, completedSteps, markStepCompleted]);
}
